import { useRef, useEffect, useCallback } from 'react';
import { EditorEvent, Editor as TinyMCEEditor } from 'tinymce';

export const useEditorSetup = (
  content: string,
  onChange: (content: string) => void,
  onMediaDelete?: (images: HTMLImageElement[]) => void,
) => {
  const editorInstanceRef = useRef<TinyMCEEditor | null>(null);
  const isInitializedRef = useRef(false);
  const prevContentRef = useRef(content);
  const prevOnChangeRef = useRef(onChange);

  const handleImageDelete = useCallback(
    (element: HTMLElement) => {
      if (!onMediaDelete || element.tagName !== 'IMG') return;
      onMediaDelete([element as HTMLImageElement]);
    },
    [onMediaDelete],
  );
  useEffect(() => {
    prevOnChangeRef.current = onChange;

    if (content !== prevContentRef.current) {
      prevContentRef.current = content;
      if (editorInstanceRef.current && isInitializedRef.current) {
        const currentContent = editorInstanceRef.current.getContent();
        if (currentContent !== content) {
          editorInstanceRef.current.setContent(content);
        }
      }
    }
  }, [onChange, content]);

  const handleEditorInit = useCallback(
    (editor: TinyMCEEditor) => {
      editorInstanceRef.current = editor;
      editor.on('init', () => {
        if (!isInitializedRef.current) {
          editor.setContent(prevContentRef.current);
          isInitializedRef.current = true;
        }
      });

      editor.on('change input', () => {
        const newContent = editor.getContent();
        if (newContent !== prevContentRef.current) {
          prevContentRef.current = newContent;
          prevOnChangeRef.current(newContent);
        }
      });

      // Handle image deletion
      editor.on('NodeRemove', (e: EditorEvent<HTMLElement>) => {
        if (e.target instanceof HTMLElement) {
          handleImageDelete(e.target);
        }
      });
    },
    [handleImageDelete],
  );

  return {
    editorInstanceRef,
    isInitializedRef,
    handleEditorInit,
  };
};

export type UseEditorSetupReturnType = {
  editorInstanceRef: React.RefObject<TinyMCEEditor | null>;
  isInitializedRef: React.RefObject<boolean>;
  handleEditorInit: (editor: TinyMCEEditor) => void;
};
